// useHttp.ts
import { useState, useCallback } from 'react';

interface UseHttpReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  sendRequest: () => Promise<T | void>;
}

/**
 * useHttp accepts a function that returns a promise (typically an HTTP request)
 * and provides a way to call it while managing loading, error, and data states.
 */
export function useHttp<T>(requestFn: () => Promise<T>): UseHttpReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendRequest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = (await requestFn() as any); // TODO: get rid of casting to "any"
      if ("isError" in response && response["isError"]) {
        const er = response["firstError"]["description"]
        setError(er);
        alert(er);
        throw new Error(er);
      }
      const value = response["value"];
      setData(value);
      return value;
    } catch (err: any) {
      const errMsg = err.message || 'Something went wrong';
      setError(errMsg);
      alert(errMsg);
    } finally {
      setLoading(false);
    }
  }, [requestFn]);

  return {data, loading, error, sendRequest };
}
